// Copyright (c) 2026 Solartek LLC. All rights reserved.
/**
 * Flowza Surveillance integration (docs/integrations/flowza-surveillance.md, migration 20261102000100). The wire contract is
 * docs/protocol.md of the Flowza Surveillance repository (Parts B and C); this file holds FlowZa Time's side of it:
 *
 *  - the INBOUND schemas Flowza Surveillance posts to `/api/v1/inbound/surveillance/*` (pairing, daily reports, live snapshots),
 *    every field bounded — the organisation always comes from the API key, never from a body;
 *  - the Settings → Flowza Surveillance DTOs (status, display settings, pairing, the launch into the Surveillance console);
 *  - the Productivity dashboard, the employee drill-down, the anomalies and the employee's own summary.
 *
 * Window titles and screenshots never come to FlowZa Time: a report carries application names, domains, categories and totals.
 */
import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from './common.js';
// ----- vocabulary ------------------------------------------------------------------------------------------------------------
/** Keys that open the Flowza Surveillance console (launch token `perms`, docs/protocol.md §C.2). */
export const SURVEILLANCE_CONSOLE_PERMISSIONS = ['surveillance.admin', 'productivity.view', 'productivity.view_team', 'surveillance.view_screenshots', 'surveillance.capture'];
export const PRODUCTIVITY_LEVELS = ['productive', 'neutral', 'unproductive'];
export const PRESENCE_STATUSES = ['working', 'idle', 'locked', 'offline'];
export const ANOMALY_SEVERITIES = ['low', 'medium', 'high'];
export const ANOMALY_STATUSES = ['open', 'acknowledged', 'dismissed'];
/** The kinds Flowza Surveillance sends today (§B.4). A later kind is stored as sent and shown with a generic label. */
export const KNOWN_ANOMALY_KINDS = ['idle_spike', 'productivity_drop', 'after_hours', 'unproductive_spike', 'synthetic_input', 'agent_offline', 'tamper', 'new_remote_tool', 'batch_gap'];
export const SURVEILLANCE_LINK_STATUSES = ['not_connected', 'connected', 'disconnected'];
export const SURVEILLANCE_LIMITS = {
    /** Reports per POST /daily-reports. */
    reportsPerRequest: 200,
    /** Employees per snapshot (larger organisations send several). */
    snapshotEmployees: 5000,
    snapshotAnomalies: 500,
    /** Request body of any inbound call. */
    bodyBytes: 5 * 1024 * 1024,
    /** Employees per directory page. */
    directoryPage: 500,
    /** Longest range of the dashboard and the drill-down (days, both ends included). */
    rangeDays: 92,
    /** Pairing code lifetime (minutes) and replay window of signed requests (seconds). */
    pairingMinutes: 10,
    clockSkewSeconds: 300,
    nonceWindowMinutes: 15,
    /** Launch tokens live this long (seconds). */
    launchTokenSeconds: 60,
    /** The API key the link uses (days). */
    apiKeyDays: 730,
    /** Requests per minute per API key / per client address. */
    perKeyPerMinute: 120,
    perIpPerMinute: 600,
};
// ----- inbound (Flowza Surveillance → FlowZa Time) -------------------------------------------------------------------------
/** A raw Ed25519 public key (32 bytes) in base64url without padding. */
export const ed25519PublicKeySchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Expected a base64url Ed25519 public key');
const httpUrlSchema = z.url().max(300).refine((u) => /^https?:\/\//i.test(u), 'Expected an http(s) URL');
const secondsSchema = z.number().int().min(0).max(90_000);
const shortText = (max) => z.string().trim().min(1).max(max);
const scalarSchema = z.union([z.number(), z.string().max(300), z.boolean(), z.null()]);
export const surveillancePairRequestSchema = z.object({
    /** 32 random bytes, base64url (43 characters). */
    code: z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Expected a pairing code'),
    organizationId: uuidSchema,
    surveillance: z.object({
        keyId: z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/),
        publicKey: ed25519PublicKeySchema,
        apiUrl: httpUrlSchema,
        webUrl: httpUrlSchema,
        version: z.string().max(40).optional(),
        capabilities: z.record(z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,39}$/), z.union([z.boolean(), z.string().max(40)]))
            .refine((v) => Object.keys(v).length <= 20, 'At most 20 capabilities').optional(),
    }),
});
export const surveillanceAnomalySchema = z.object({
    id: shortText(64),
    employeeId: uuidSchema,
    date: isoDateSchema,
    kind: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'Expected a snake_case kind'),
    severity: z.enum(ANOMALY_SEVERITIES),
    title: shortText(200),
    detectedAt: isoDateTimeSchema,
    status: z.enum(ANOMALY_STATUSES).optional(),
    details: z.record(z.string().max(64), scalarSchema)
        .refine((v) => Object.keys(v).length <= 30, 'At most 30 detail fields')
        .refine((v) => JSON.stringify(v).length <= 4096, 'Details larger than 4 KB').optional(),
});
const appUsageSchema = z.object({ name: shortText(200), category: z.string().max(60).nullable().optional(), productivity: z.enum(PRODUCTIVITY_LEVELS), seconds: secondsSchema });
const categoryUsageSchema = z.object({ name: shortText(60), productivity: z.enum(PRODUCTIVITY_LEVELS), seconds: secondsSchema });
const siteUsageSchema = z.object({ domain: shortText(253), category: z.string().max(60).nullable().optional(), productivity: z.enum(PRODUCTIVITY_LEVELS), seconds: secondsSchema });
export const surveillanceDailyReportSchema = z.object({
    employeeId: uuidSchema,
    date: isoDateSchema,
    timezone: z.string().min(1).max(64),
    periodStart: isoDateTimeSchema,
    periodEnd: isoDateTimeSchema,
    version: z.number().int().min(1).max(1_000_000),
    generatedAt: isoDateTimeSchema,
    totals: z.object({
        trackedSeconds: secondsSchema,
        activeSeconds: secondsSchema,
        idleSeconds: secondsSchema,
        productiveSeconds: secondsSchema,
        neutralSeconds: secondsSchema,
        unproductiveSeconds: secondsSchema,
        scheduledSeconds: secondsSchema.optional(),
        offlineDuringScheduleSeconds: secondsSchema.optional(),
        afterHoursActiveSeconds: secondsSchema.optional(),
    }),
    score: z.number().int().min(0).max(100).nullable(),
    utilization: z.number().int().min(0).max(1000).nullable().optional(),
    firstActivityAt: isoDateTimeSchema.nullable().optional(),
    lastActivityAt: isoDateTimeSchema.nullable().optional(),
    hourly: z.array(z.object({ hour: z.number().int().min(0).max(23), activeSeconds: z.number().int().min(0).max(3600), productiveSeconds: z.number().int().min(0).max(3600) })).max(24)
        .refine((v) => new Set(v.map((h) => h.hour)).size === v.length, 'Each hour at most once'),
    topApps: z.array(appUsageSchema).max(10).default([]),
    topCategories: z.array(categoryUsageSchema).max(12).default([]),
    topSites: z.array(siteUsageSchema).max(10).default([]),
    screenshotCount: z.number().int().min(0).max(100_000).default(0),
    devices: z.array(z.object({ hostname: shortText(200), os: z.string().max(20), coveragePercent: z.number().int().min(0).max(100) })).max(5).default([]),
    anomalies: z.array(surveillanceAnomalySchema).max(20).default([]),
}).refine((r) => Date.parse(r.periodEnd) > Date.parse(r.periodStart), { message: 'periodEnd must be after periodStart', path: ['periodEnd'] });
export const surveillanceDailyReportsRequestSchema = z.object({
    reports: z.array(surveillanceDailyReportSchema).min(1).max(SURVEILLANCE_LIMITS.reportsPerRequest),
});
export const surveillanceSnapshotRequestSchema = z.object({
    capturedAt: isoDateTimeSchema,
    employees: z.array(z.object({
        employeeId: uuidSchema,
        status: z.enum(PRESENCE_STATUSES),
        since: isoDateTimeSchema.nullable().optional(),
        lastSeenAt: isoDateTimeSchema.nullable().optional(),
        app: z.string().max(200).nullable().optional(),
        category: z.string().max(60).nullable().optional(),
        productivity: z.enum(PRODUCTIVITY_LEVELS).nullable().optional(),
        today: z.object({ activeSeconds: secondsSchema, productiveSeconds: secondsSchema, score: z.number().int().min(0).max(100).nullable() }),
    })).max(SURVEILLANCE_LIMITS.snapshotEmployees),
    anomalies: z.array(surveillanceAnomalySchema).max(SURVEILLANCE_LIMITS.snapshotAnomalies).default([]),
});
export const surveillanceDirectoryQuerySchema = z.object({
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(SURVEILLANCE_LIMITS.directoryPage).default(SURVEILLANCE_LIMITS.directoryPage),
});
/** Employee statuses of the directory (§B.3). */
export const DIRECTORY_EMPLOYEE_STATUSES = ['active', 'on_leave', 'suspended', 'terminated', 'resigned', 'inactive'];
export const DEFAULT_SURVEILLANCE_LINK_SETTINGS = { employeeSelfView: true, showLivePresence: true };
/** PATCH: explicit optional fields, no defaults — a one-field PATCH changes one field (AGENTS.md, Zod pitfalls). */
export const surveillanceSettingsPatchSchema = z.object({
    employeeSelfView: z.boolean().optional(),
    showLivePresence: z.boolean().optional(),
}).refine((v) => v.employeeSelfView !== undefined || v.showLivePresence !== undefined, 'Change at least one setting.');
// ----- Productivity (dashboard, drill-down, anomalies, self-view) ---------------------------------------------------------
export const productivityRangeQuerySchema = z.object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    departmentId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
});
export const productivityEmployeesQuerySchema = productivityRangeQuerySchema.extend({
    search: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    sort: z.enum(['name', 'score', 'active', 'productive', 'idle', 'anomalies']).default('score'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const productivityAnomaliesQuerySchema = productivityRangeQuerySchema.extend({
    employeeId: uuidSchema.optional(),
    severity: z.enum(ANOMALY_SEVERITIES).optional(),
    status: z.enum(ANOMALY_STATUSES).optional(),
    kind: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/).optional(),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const myProductivityQuerySchema = z.object({ from: isoDateSchema.optional(), to: isoDateSchema.optional() });
/** Productivity against attendance (FlowZa Time's own daily record): the cross-check of the drill-down. */
export const PRODUCTIVITY_CROSS_CHECKS = ['present_low_activity', 'activity_while_absent', 'activity_on_leave'];
/** "Present but under 1 hour of computer activity" (seconds). */
export const PRESENT_LOW_ACTIVITY_SECONDS = 3600;
//# sourceMappingURL=surveillance.js.map